import {HubConnectionBuilder, HubConnectionState, HttpTransportType, LogLevel} from "@microsoft/signalr";
import {isPublicSalesEventStateCurrent} from './public-sales-event-product-state';

const hubUrl = "/api/v2/hubs/public-sales-events";

let connection = null;
let startPromise = null;
let connectionRetryTimer = null;
let connectionRetryAttempts = 0;
let connectionEpoch = 0;
let browserCatchUpListenersRegistered = false;
const MAX_SUBSCRIPTION_ATTEMPTS = 5;
const productSubscriptions = new Map();
const summaryListeners = new Map();
const invalidationListeners = new Map();
const reconnectListeners = new Map();
const subscriptionRetryAttempts = new Map();
const subscriptionRetryTimers = new Map();
const subscriptionPromises = new Map();
const unsubscriptionPromises = new Map();
const subscriptionStates = new Map();
const settledSubscriptions = new Set();
const subscriptionSummaries = new Map();
const connectionStateListeners = new Set();
let recoveryPromise = null;
let browserCatchUpPromise = null;
let browserWasHidden = false;
let idleStopTimer = null;
let idleStopPromise = null;
const subscriptionSnapshots = new Map();

function setSubscriptionState(key, state) {
    // A late subscription acknowledgement must not override browser offline.
    if (state === 'live' && isOffline()) state = 'offline';
    if (!productSubscriptions.has(key) || subscriptionStates.get(key) === state) return;
    subscriptionStates.set(key, state);
    connectionStateListeners.forEach(listener => listener());
}

function setConnectionState(state) {
    let changed = false;
    productSubscriptions.forEach((_, key) => {
        if (subscriptionStates.get(key) === state) return;
        subscriptionStates.set(key, state);
        changed = true;
    });
    if (changed) connectionStateListeners.forEach(listener => listener());
}

function isOffline() {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// Observers share the existing transport; reading health never joins a group or
// starts requests. Scope by lot so one rejected join cannot taint healthy lots.
export function getPublicSalesEventConnectionState(productUuid, lotId = null) {
    const key = subscriptionKey(normalizeProductUuid(productUuid), lotId == null ? null : Number(lotId));
    if (settledSubscriptions.has(key)) return 'settled';
    return subscriptionStates.get(key) ?? 'connecting';
}

function recordSubscriptionOutcome(key, message) {
    if (!productSubscriptions.has(key)) return;
    const previous = subscriptionSummaries.get(key);
    if (!message?.summary || !isPublicSalesEventStateCurrent(previous, message.summary)) return;
    subscriptionSummaries.set(key, message.summary);
    subscriptionSnapshots.set(key, {message, receivedAt: Date.now()});
    const outcome = String(message?.summary?.lotStatus ?? '').toLowerCase().replace(/[\s_-]+/g, '');
    const settled = ['sold', 'passedin', 'salefellthrough', 'withdrawn', 'cancelled', 'canceled'].includes(outcome);
    if (settled === settledSubscriptions.has(key)) return;
    if (settled) settledSubscriptions.add(key);
    else settledSubscriptions.delete(key);
    connectionStateListeners.forEach(listener => listener());
}

export function observePublicSalesEventConnection(listener) {
    connectionStateListeners.add(listener);
    return () => connectionStateListeners.delete(listener);
}

export function getPublicSalesEventsConnectionState() {
    const states = [...productSubscriptions.keys()].map(key => {
        if (settledSubscriptions.has(key)) return 'settled';
        return subscriptionStates.get(key) ?? 'connecting';
    });
    return ['offline', 'interrupted', 'retrying', 'connecting'].find(state => states.includes(state)) ?? 'live';
}

export function hasActivePublicSalesEvents() {
    return [...productSubscriptions.keys()].some(key => !settledSubscriptions.has(key));
}

function getListeners(map, key) {
    if (!map.has(key)) {
        map.set(key, new Set());
    }

    return map.get(key);
}

function dispatchTo(map, productUuid, message) {
    if (map === summaryListeners) recordSubscriptionOutcome(productUuid, message);
    const listeners = map.get(productUuid);
    if (!listeners) return;
    listeners.forEach(listener => listener?.(message));
}

function normalizeProductUuid(productUuid) {
    return typeof productUuid === 'string' ? productUuid.toLowerCase() : null;
}

function subscriptionKey(productUuid, lotId = null) {
    return JSON.stringify([productUuid, lotId]);
}

function dispatchMessage(map, message) {
    const productUuid = normalizeProductUuid(message?.productUuid);
    if (!productUuid) return;
    dispatchTo(map, subscriptionKey(productUuid), message);
    const lotId = Number(message?.summary?.lotId ?? message?.lotId);
    if (Number.isSafeInteger(lotId) && lotId > 0) {
        dispatchTo(map, subscriptionKey(productUuid, lotId), message);
    }
}

async function resubscribeToProducts() {
    const productUuids = [...productSubscriptions.keys()];
    const results = await Promise.allSettled(
        productUuids.map(productUuid => subscribeConnectionToProductWithRetry(productUuid))
    );
    results.forEach((result) => {
        if (result.status === 'rejected') console.log(result.reason);
    });
    return productUuids;
}

async function subscribeConnectionToProduct(key) {
    const [productUuid, lotId] = JSON.parse(key);
    const activeConnection = connection;
    const subscribeEpoch = connectionEpoch;
    if (activeConnection?.state !== HubConnectionState.Connected) {
        throw new Error('Public sales event connection is not ready');
    }
    const currentState = lotId === null
        ? await activeConnection.invoke("SubscribeToProduct", productUuid)
        : await activeConnection.invoke("SubscribeToLot", productUuid, lotId);
    if (activeConnection !== connection || subscribeEpoch !== connectionEpoch
        || activeConnection.state !== HubConnectionState.Connected) {
        throw new Error('Public sales event connection changed during subscription');
    }
    const messageProductUuid = normalizeProductUuid(currentState?.productUuid);
    if (messageProductUuid !== productUuid
        || (lotId !== null && Number(currentState?.summary?.lotId) !== lotId)) {
        // The hub removes a product from its group when it cannot return a
        // snapshot. A null or mismatched response is not an acknowledged join.
        throw new Error(`Public sales event subscription was not acknowledged for ${productUuid}`);
    }
    dispatchTo(summaryListeners, key, currentState);
    setSubscriptionState(key, 'live');
    return currentState;
}

function clearSubscriptionRetry(productUuid) {
    const timer = subscriptionRetryTimers.get(productUuid);
    if (timer) clearTimeout(timer);
    subscriptionRetryTimers.delete(productUuid);
    subscriptionRetryAttempts.delete(productUuid);
}

function scheduleSubscriptionRetry(productUuid) {
    if (!productSubscriptions.has(productUuid) || subscriptionRetryTimers.has(productUuid)) return;

    const attempt = (subscriptionRetryAttempts.get(productUuid) ?? 0) + 1;
    subscriptionRetryAttempts.set(productUuid, attempt);
    if (attempt >= MAX_SUBSCRIPTION_ATTEMPTS) {
        // Leave a failed product quiet until reconnect, focus or another
        // browser lifecycle event. The HTTP catch-up is a one-off, not polling.
        dispatchTo(reconnectListeners, productUuid);
        setSubscriptionState(productUuid, isOffline() ? 'offline' : 'interrupted');
        return;
    }
    setSubscriptionState(productUuid, isOffline() ? 'offline' : 'retrying');
    const delay = Math.min(1000 * (2 ** (attempt - 1)), 15000);
    const timer = setTimeout(() => {
        subscriptionRetryTimers.delete(productUuid);
        ensureStarted()
            .then(activeConnection => activeConnection.state === HubConnectionState.Connected
                ? subscribeConnectionToProductWithRetry(productUuid)
                : Promise.reject(new Error('Public sales event connection is not ready')))
            .catch((error) => console.log(error));
    }, delay);
    subscriptionRetryTimers.set(productUuid, timer);
}

async function subscribeConnectionToProductWithRetry(productUuid) {
    if (!productSubscriptions.has(productUuid)) return null;
    if (subscriptionPromises.has(productUuid)) return subscriptionPromises.get(productUuid);
    const subscribeEpoch = connectionEpoch;
    const pendingUnsubscription = unsubscriptionPromises.get(productUuid);
    const groupJoin = pendingUnsubscription
        ? pendingUnsubscription.then(() => productSubscriptions.has(productUuid)
            ? subscribeConnectionToProduct(productUuid)
            : null)
        : subscribeConnectionToProduct(productUuid);
    const subscriptionPromise = groupJoin
        .then((currentState) => {
            clearSubscriptionRetry(productUuid);
            return currentState;
        })
        .catch((error) => {
            if (subscribeEpoch === connectionEpoch) scheduleSubscriptionRetry(productUuid);
            throw error;
        });
    subscriptionPromises.set(productUuid, subscriptionPromise);
    subscriptionPromise.then(() => {
        if (subscriptionPromises.get(productUuid) === subscriptionPromise) subscriptionPromises.delete(productUuid);
    }, () => {
        if (subscriptionPromises.get(productUuid) === subscriptionPromise) subscriptionPromises.delete(productUuid);
    });
    return subscriptionPromise;
}

function unsubscribeConnectionFromProduct(key) {
    const [productUuid, lotId] = JSON.parse(key);
    const activeConnection = connection;
    if (activeConnection?.state !== HubConnectionState.Connected) return;
    const unsubscriptionPromise = (lotId === null
        ? activeConnection.invoke('UnsubscribeFromProduct', productUuid)
        : activeConnection.invoke('UnsubscribeFromLot', productUuid, lotId))
        .catch((error) => console.log(error));
    unsubscriptionPromises.set(key, unsubscriptionPromise);
    unsubscriptionPromise.then(() => {
        if (unsubscriptionPromises.get(key) === unsubscriptionPromise) {
            unsubscriptionPromises.delete(key);
        }
    });
}

function clearConnectionRetry() {
    if (connectionRetryTimer) clearTimeout(connectionRetryTimer);
    connectionRetryTimer = null;
    connectionRetryAttempts = 0;
}

function pauseSubscriptionRetries() {
    subscriptionRetryTimers.forEach((timer) => clearTimeout(timer));
    subscriptionRetryTimers.clear();
    subscriptionRetryAttempts.clear();
    subscriptionPromises.clear();
    unsubscriptionPromises.clear();
}

function scheduleConnectionRetry() {
    if (!productSubscriptions.size || connectionRetryTimer) return;
    connectionRetryAttempts += 1;
    if (connectionRetryAttempts >= MAX_SUBSCRIPTION_ATTEMPTS) {
        setConnectionState(isOffline() ? 'offline' : 'interrupted');
    }
    // One socket attempt per tab, with capped backoff and jitter. No catalogue
    // HTTP request is made by this timer on a healthy connection.
    const baseDelay = Math.min(1000 * (2 ** Math.min(connectionRetryAttempts - 1, 6)), 60000);
    const delay = baseDelay + Math.round(baseDelay * Math.random() * 0.2);
    connectionRetryTimer = setTimeout(() => {
        connectionRetryTimer = null;
        if (!productSubscriptions.size) return;
        ensureStarted()
            .then((activeConnection) => {
                if (activeConnection.state !== HubConnectionState.Connected) {
                    scheduleConnectionRetry();
                    return;
                }
                resubscribeToProducts().then((productUuids) => {
                    productUuids.forEach(productUuid => dispatchTo(reconnectListeners, productUuid));
                }).catch((error) => console.log(error));
            })
            .catch((error) => console.log(error));
    }, delay);
}

async function catchUpActiveSubscriptions() {
    if (!productSubscriptions.size) return;

    const productUuids = [...productSubscriptions.keys()];
    productUuids.forEach(productUuid => subscriptionRetryAttempts.delete(productUuid));
    try {
        const activeConnection = await ensureStarted();
        if (activeConnection.state === HubConnectionState.Connected) {
            await resubscribeToProducts();
        }
    } catch (error) {
        console.log(error);
    }

    // A browser restore still needs an authoritative HTTP catch-up even when
    // the live connection has not recovered yet.
    productUuids.forEach(productUuid => dispatchTo(reconnectListeners, productUuid));
}

function handleBrowserCatchUp() {
    if (!connection || !productSubscriptions.size) return;
    if (browserCatchUpPromise) return browserCatchUpPromise;
    browserCatchUpPromise = catchUpActiveSubscriptions()
        .catch(error => console.log(error))
        .finally(() => { browserCatchUpPromise = null; });
    return browserCatchUpPromise;
}

function registerBrowserCatchUpListeners() {
    if (browserCatchUpListenersRegistered || typeof window === 'undefined') return;

    browserCatchUpListenersRegistered = true;
    window.addEventListener('online', handleBrowserCatchUp);
    window.addEventListener('offline', () => setConnectionState('offline'));
    window.addEventListener('pageshow', event => {
        if (event.persisted) handleBrowserCatchUp();
    });

    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') {
                browserWasHidden = true;
            } else if (browserWasHidden) {
                browserWasHidden = false;
                handleBrowserCatchUp();
            }
        });
    }
}

function createConnection() {
    if (connection) return connection;

    connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets
        })
        .withAutomaticReconnect({nextRetryDelayInMilliseconds: context => {
            if (context.previousRetryCount >= 4) setConnectionState(isOffline() ? 'offline' : 'interrupted');
            return Math.min(1000 * (context.previousRetryCount + 1), 15000);
        }})
        .configureLogging(LogLevel.Warning)
        .build();

    connection.on("salesEventSummary", (message) => {
        dispatchMessage(summaryListeners, message);
    });

    connection.on("salesEventChanged", (message) => {
        dispatchMessage(invalidationListeners, message);
    });

    connection.on("salesEventDeliveryRestored", () => {
        productSubscriptions.forEach((_, productUuid) => dispatchTo(reconnectListeners, productUuid));
    });

    connection.onreconnected(async () => {
        connectionEpoch += 1;
        clearConnectionRetry();
        pauseSubscriptionRetries();
        const productUuids = await resubscribeToProducts();
        productUuids.forEach(productUuid => dispatchTo(reconnectListeners, productUuid));
    });

    connection.onreconnecting?.(() => {
        connectionEpoch += 1;
        pauseSubscriptionRetries();
        setConnectionState(isOffline() ? 'offline' : 'retrying');
    });

    connection.onclose(() => {
        connectionEpoch += 1;
        startPromise = null;
        pauseSubscriptionRetries();
        setConnectionState(isOffline() ? 'offline' : 'retrying');
        productSubscriptions.forEach((_, productUuid) => dispatchTo(reconnectListeners, productUuid));
        if (!recoveryPromise) scheduleConnectionRetry();
    });

    return connection;
}

async function ensureStarted() {
    if (idleStopPromise) await idleStopPromise;
    const activeConnection = createConnection();
    if (activeConnection.state === HubConnectionState.Connected) {
        return activeConnection;
    }
    if (activeConnection.state === HubConnectionState.Connecting) {
        return startPromise ?? activeConnection;
    }
    if (activeConnection.state === HubConnectionState.Reconnecting) {
        return activeConnection;
    }

    if (!startPromise) {
        startPromise = activeConnection.start()
            .then(() => {
                clearConnectionRetry();
                return activeConnection;
            })
            .catch((error) => {
                startPromise = null;
                setConnectionState(isOffline() ? 'offline' : 'retrying');
                scheduleConnectionRetry();
                throw error;
            });
    }

    return startPromise;
}

export function subscribeToPublicSalesEvents(productUuid, {
    lotId = null,
    onInvalidation,
    onReconnect,
    onSummary
} = {}) {
    if (!productUuid) {
        return () => {};
    }

    const normalizedProductUuid = normalizeProductUuid(productUuid);
    const scopedLotId = lotId === null || lotId === undefined ? null : Number(lotId);
    if (!normalizedProductUuid || (scopedLotId !== null && (!Number.isSafeInteger(scopedLotId) || scopedLotId <= 0))) {
        return () => {};
    }
    const key = subscriptionKey(normalizedProductUuid, scopedLotId);
    if (idleStopTimer !== null) clearTimeout(idleStopTimer);
    idleStopTimer = null;
    const currentCount = productSubscriptions.get(key) ?? 0;
    productSubscriptions.set(key, currentCount + 1);
    if (!currentCount) setSubscriptionState(key, isOffline() ? 'offline' : 'connecting');
    registerBrowserCatchUpListeners();

    if (onSummary) getListeners(summaryListeners, key).add(onSummary);
    if (onInvalidation) getListeners(invalidationListeners, key).add(onInvalidation);
    if (onReconnect) getListeners(reconnectListeners, key).add(onReconnect);

    const snapshot = subscriptionSnapshots.get(key);
    if (currentCount && connection?.state === HubConnectionState.Connected
        && subscriptionStates.get(key) === 'live' && snapshot) {
        if (onSummary) {
            const elapsed = Math.max(0, Math.floor((Date.now() - snapshot.receivedAt) / 1000));
            const summary = {...snapshot.message.summary};
            for (const field of ['secondsRemaining', 'secondsToStart']) {
                if (Number.isFinite(summary[field])) summary[field] = Math.max(0, summary[field] - elapsed);
            }
            onSummary({...snapshot.message, summary});
        }
    } else {
        ensureStarted()
            .then(() => productSubscriptions.has(key)
                ? subscribeConnectionToProductWithRetry(key)
                : null)
            .catch((error) => console.log(error));
    }

    let unsubscribed = false;
    return () => {
        if (unsubscribed) return;
        unsubscribed = true;
        const nextCount = (productSubscriptions.get(key) ?? 1) - 1;
        if (nextCount <= 0) {
            productSubscriptions.delete(key);
            subscriptionStates.delete(key);
            settledSubscriptions.delete(key);
            subscriptionSummaries.delete(key);
            subscriptionSnapshots.delete(key);
            connectionStateListeners.forEach(listener => listener());
            clearSubscriptionRetry(key);
            if (!productSubscriptions.size) {
                clearConnectionRetry();
                scheduleIdleStop();
            }
            const pendingSubscription = subscriptionPromises.get(key);
            if (pendingSubscription) {
                const unsubscribeIfStillUnused = () => {
                    if (!productSubscriptions.has(key)) {
                        unsubscribeConnectionFromProduct(key);
                    }
                };
                pendingSubscription.then(unsubscribeIfStillUnused, unsubscribeIfStillUnused);
            } else {
                unsubscribeConnectionFromProduct(key);
            }
        } else {
            productSubscriptions.set(key, nextCount);
        }

        if (onSummary) summaryListeners.get(key)?.delete(onSummary);
        if (onInvalidation) invalidationListeners.get(key)?.delete(onInvalidation);
        if (onReconnect) reconnectListeners.get(key)?.delete(onReconnect);
        if (nextCount <= 0) {
            summaryListeners.delete(key);
            invalidationListeners.delete(key);
            reconnectListeners.delete(key);
        }
    };
}

function scheduleIdleStop() {
    if (idleStopTimer !== null) clearTimeout(idleStopTimer);
    idleStopTimer = setTimeout(() => {
        idleStopTimer = null;
        if (productSubscriptions.size) return;
        idleStopPromise = (async () => {
            // Finish pending joins/leaves before stopping. New consumers wait for
            // this stop, then start once; navigation cannot strand their socket.
            await Promise.allSettled([startPromise, ...subscriptionPromises.values(), ...unsubscriptionPromises.values()]);
            if (!productSubscriptions.size && connection?.state !== HubConnectionState.Disconnected) {
                await connection?.stop();
            }
        })().catch(error => console.log(error)).finally(() => { idleStopPromise = null; });
    }, 1000);
}

// Manual recovery restarts transport but does not create an HTTP polling loop.
export function reconnectPublicSalesEvents() {
    return ensureStarted();
}

// A single explicit retry resets exhausted joins, including when SignalR itself
// is still Connected. Do not stop a healthy shared socket or rejoin healthy lots.
export function recoverPublicSalesEvents() {
    if (recoveryPromise) return recoveryPromise;
    const keys = [...productSubscriptions.keys()].filter(key => subscriptionStates.get(key) !== 'live');
    keys.forEach(key => {
        clearSubscriptionRetry(key);
        setSubscriptionState(key, isOffline() ? 'offline' : 'retrying');
    });
    // Yield before stopping so simultaneous clicks and onclose share this owner.
    recoveryPromise = Promise.resolve().then(async () => {
        const currentConnection = createConnection();
        let retryKeys = keys;
        if (currentConnection.state === HubConnectionState.Reconnecting) {
            // Cancel SignalR's backoff, then start once immediately. Stopping
            // loses every group membership, so restore all current scopes.
            await currentConnection.stop();
            clearConnectionRetry();
            retryKeys = [...productSubscriptions.keys()];
        }
        if (!productSubscriptions.size) return;
        const activeConnection = await ensureStarted();
        if (activeConnection.state !== HubConnectionState.Connected) return;
        return Promise.allSettled(retryKeys.map(key => subscribeConnectionToProductWithRetry(key)));
    }).finally(() => {
        recoveryPromise = null;
        // A second disconnect during recovery must not leave retries paused.
        if (connection?.state === HubConnectionState.Disconnected) scheduleConnectionRetry();
    });
    return recoveryPromise;
}
